import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ErrorPage } from '@zg-rentals/error-page-components';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { createAuthPredicates } from '@zg-rentals/auth-comet/mfa';
import { defineAuthProvider } from '@zg-rentals/auth-react/legacy';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { RentalManagerUserInfoV2WebApiHandler_get_queryOptions } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import type { RedirectHref } from '@zg-rentals/auth-react/legacy';
import { rentalsLoginOptions, rentalsStepUpLoginOptions } from '@zg-rentals/login-zillow';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import UserActions from '@common/actions/UserActions';
import UserModel from '@common/models/User';
import { useAppDispatch } from '../../../store/hooks';
import type { RootState } from '../../../store/types';

const APP_NAME = 'rent-payment-web';
const ManagerAuthEnabledContext = createContext(false);

const httpClient = getClient().extend({
  prefixUrl: __DEV__ ? '/rental-manager/payments/proxy' : process.env.NEXT_PUBLIC_RENTAL_MANAGER_PREFIX_URL,
});

type User = ReturnType<typeof UserModel>;

type ManagerAuthProps = {
  initialUser: User;
};

const { hasInitiatedAuth, hasAuth, hasStrongAuth } = createAuthPredicates<User>((user) => ({
  ...user,
  isLoggedIn: user.loggedIn,
  hasStrongAuthentication: user.hasStrongAuthentication && user.sessionExpirationDate >= Date.now(),
}));

const ManagerAuth = defineAuthProvider<ManagerAuthProps, User>({
  queryKey: ['rent-payment-web', 'manager-auth'],
  queryOptions: ({ initialUser }) => {
    const options = RentalManagerUserInfoV2WebApiHandler_get_queryOptions(
      { pageWithSensitiveInformation: true },
      { httpClient },
    );

    return {
      ...options,
      queryFn: async () => UserModel(await options.queryFn()),
      initialData: hasInitiatedAuth(initialUser) ? initialUser : undefined,
      staleTime: 0,
    };
  },
  isAuthorized: hasInitiatedAuth,
});

const ctaText = 'Return to Zillow Rental Manager';
const ctaUrl = '/rental-manager';

const onError = (errorMessage: string, errorCode: number) => (
  <ErrorPage
    errorMessage={errorMessage}
    errorDetails={`Error code: ${errorCode}`}
    action={<a href={ctaUrl}>{ctaText}</a>}
  />
);

const loginRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalsLoginOptions,
});

const mfaStepUpRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalsLoginOptions,
  ...rentalsStepUpLoginOptions,
});

const mfaRedirectHref: RedirectHref = (href, params) => {
  getGlobalMonitor()?.count(
    { name: 'mfa step-up attempt total' },
    { name: 'mfa step-up attempt type MfaStepUpAttempt' },
  );

  return mfaStepUpRedirectHref(href, params);
};

const onMfaRedirectAuthorized = () =>
  getGlobalMonitor()?.count(
    { name: 'mfa step-up success total' },
    { name: 'mfa step-up success type MfaStepUpSuccess' },
  );

const defaultBoundaryErrorProps = {
  onError: onError('unknown error', 500),
  onUnauthorized: () => onError('unauthorized', 401),
};

export const useManagerAuthEnabled = () => useContext(ManagerAuthEnabledContext);

type ManagerBoundaryProps = {
  children: ReactNode;
  redirecting?: ReactNode;
};

type ManagerAuthBoundaryProps = ManagerBoundaryProps & {
  loading?: ReactNode;
};

export const ManagerAuthProvider = ({ children, loading, redirecting }: ManagerAuthBoundaryProps) => {
  const user = useSelector((state: RootState) => state.user);
  const initialUser = useMemo(() => UserModel(user), [user]);

  return (
    <ManagerAuthEnabledContext.Provider value>
      <ManagerAuth.Provider initialUser={initialUser}>
        <ManagerAuthSyncer />
        {typeof window !== 'undefined' ? (
          <ManagerAuthBoundary loading={loading} redirecting={redirecting}>
            {children}
          </ManagerAuthBoundary>
        ) : (
          children
        )}
      </ManagerAuth.Provider>
    </ManagerAuthEnabledContext.Provider>
  );
};

function ManagerAuthSyncer() {
  const dispatch = useAppDispatch();
  const { user } = ManagerAuth.useAuth();

  useEffect(() => {
    if (user) {
      dispatch(UserActions.updateUserInfo(user));
    }
  }, [dispatch, user]);

  return null;
}

export const ManagerAuthBoundary = ({
  children,
  loading = <>Loading...</>,
  redirecting = <>Redirecting...</>,
}: ManagerAuthBoundaryProps) => {
  return (
    <ManagerAuth.Boundary
      {...defaultBoundaryErrorProps}
      loading={loading}
      redirectHref={loginRedirectHref}
      redirecting={redirecting}
    >
      <ManagerAuth.Boundary
        {...defaultBoundaryErrorProps}
        loading={loading}
        isAuthorized={hasAuth}
        redirectHref={mfaRedirectHref}
        redirecting={redirecting}
        onRedirectAuthorized={onMfaRedirectAuthorized}
        searchParam={['sp', 'x']}
      >
        {children}
      </ManagerAuth.Boundary>
    </ManagerAuth.Boundary>
  );
};

export const ManagerStrongAuthBoundary = ({ children, redirecting = <></> }: ManagerBoundaryProps) => {
  return (
    <ManagerAuth.Boundary
      {...defaultBoundaryErrorProps}
      isAuthorized={hasStrongAuth}
      redirectHref={mfaRedirectHref}
      redirecting={redirecting}
      onRedirectAuthorized={onMfaRedirectAuthorized}
      searchParam={['sp', 'strong']}
    >
      {children}
    </ManagerAuth.Boundary>
  );
};
